import {
  getIngredientsByIngredientStockId,
  GetProteinFormulationDict,
  GetSeedsDict,
  LayerType,
  Round,
} from 'utils/Global';
import { EPrecisionType, PrecisionFormatter } from '../constants';
import { WellIng } from 'components/Experiment/constants';
import { DrawingLayer } from 'business/models/DrawingLayer';
import {
  FragmentLayerDTO,
  ProteinFormulationDTO,
  ProteinLayerDTO,
  ProteinLayerProteinFormulationDTO,
  ScreenLayerDTO,
  SeedLayerDetailsDTO,
} from 'business/models/Models';
import { ComputeVaryAlong } from 'business/plateviewer/Generate/ProteinLayerGenerate';

export const getProteinNameByDropNumber = (
  ingredientWell: WellIng,
  dropNumber: number,
) => {
  const proteinFormulations = GetProteinFormulationDict();
  const dropList = ingredientWell?.dropList;
  const protienFormulationId = dropList?.filter((x) => x.dropNumber === dropNumber)[0]
    ?.proteinFormulationID;
  return protienFormulationId
    ? `${proteinFormulations[protienFormulationId]?.pfName} p`
    : '';
};

export const getProteinNamesByDropNumber = (
  ingredientWell: WellIng,
  dropNumber: number,
) => {
  const proteinFormulations: Record<number, ProteinFormulationDTO> =
    GetProteinFormulationDict();
  const dropList = ingredientWell?.dropList;
  const proteinFormulationIds = dropList
    ?.find((x) => x.dropNumber === dropNumber)
    ?.proteinFormulationIDs?.slice()
    .sort((a, b) => a - b);
  return (
    proteinFormulationIds
      ?.map((id) => proteinFormulations[id]?.pfName)
      .filter((pfName) => !!pfName)
      .map((pfName) => `${pfName} p`)
      .join(' + ') ?? ''
  );
};

export const getDropScreenNamesAndVolsByDropNumber = (
  layers: DrawingLayer[],
  wellData: WellIng,
  dropNumber: number,
) => {
  const drop = wellData?.dropList?.find((x) => x.dropNumber === dropNumber);
  if (!drop) {
    return [];
  }

  const screenLayers = layers.filter(
    (layer) => layer.type === LayerType.Screen && layer.targetDrop === drop.dropNumber,
  );

  return screenLayers?.map((layer) => {
    const dropLayer = drop.dropLayerList?.find((l) => l.layerId === layer.id);

    return {
      name: dropLayer
        ? (layer.layerDetail as ScreenLayerDTO)?.screenLot.experiment.expName + ' a'
        : undefined,
      volume: dropLayer?.volume || 0,
    };
  });
};

const getVaryAlongVolume = (
  layer: DrawingLayer,
  wellPositionDetails: { x: number; y: number } | undefined,
  start?: number | null,
  stop?: number | null,
  varyAlong?: number | null,
): number | null | undefined => {
  if (!wellPositionDetails || varyAlong == null) {
    return start;
  }

  return ComputeVaryAlong(
    layer,
    wellPositionDetails.y - layer.y,
    wellPositionDetails.x - layer.x,
    start,
    stop,
    varyAlong,
  );
};

export const getDropProteinVolumesByDropNumber = (
  layers: DrawingLayer[],
  wellData: WellIng,
  dropNumber: number,
  wellPositionDetails?: { x: number; y: number },
): Array<{
  proteinName?: string;
  proteinVolume?: number | null;
  wellVolume?: number | null;
}> => {
  const drop = wellData?.dropList?.find((x) => x.dropNumber === dropNumber);
  if (!drop) {
    return [];
  }

  const proteinFormulations = GetProteinFormulationDict();

  const proteinLayers = layers.filter(
    (layer) => layer.type === LayerType.Protein && layer.targetDrop === dropNumber,
  );

  return proteinLayers
    .filter((layer) => drop.dropLayerList?.some((dl) => dl.layerId === layer.id))
    .flatMap((layer) => {
      const proteinLayerDetail = layer.layerDetail as ProteinLayerDTO;
      return (proteinLayerDetail?.proteinLayerProteinFormulation ?? []).map(
        (plpf: ProteinLayerProteinFormulationDTO) => ({
          proteinFormulationId: plpf.proteinFormulationId,
          proteinName: proteinFormulations[plpf.proteinFormulationId]?.pfName,
          proteinVolume: getVaryAlongVolume(
            layer,
            wellPositionDetails,
            plpf.startProteinDropVolume,
            plpf.stopProteinDropVolume,
            plpf.proteinDropVolumeVaryAlong,
          ),
          wellVolume: getVaryAlongVolume(
            layer,
            wellPositionDetails,
            plpf.startWellDropVolume,
            plpf.stopWellDropVolume,
            plpf.wellDropVolumeVaryAlong,
          ),
        }),
      );
    })
    .sort((a, b) => a.proteinFormulationId - b.proteinFormulationId)
    .map(({ proteinName, proteinVolume, wellVolume }) => ({
      proteinName,
      proteinVolume,
      wellVolume,
    }));
};

export const getDropSeedVolumesByDropNumber = (
  layers: DrawingLayer[],
  wellData: WellIng,
  dropNumber: number,
  wellPositionDetails?: { x: number; y: number },
): Array<{ seedVolume?: number | null; wellVolume?: number | null }> => {
  const drop = wellData?.dropList?.find((x) => x.dropNumber === dropNumber);
  if (!drop) {
    return [];
  }

  const seedLayers = layers.filter(
    (layer) => layer.type === LayerType.Seed && layer.targetDrop === dropNumber,
  );

  return seedLayers
    .filter((layer) => drop.dropLayerList?.some((dl) => dl.layerId === layer.id))
    .map((layer) => {
      const seedLayerDetail = layer.layerDetail as SeedLayerDetailsDTO;
      return {
        seedVolume: getVaryAlongVolume(
          layer,
          wellPositionDetails,
          seedLayerDetail?.startSeedDropVolume,
          seedLayerDetail?.stopSeedDropVolume,
          seedLayerDetail?.seedDropVolumeVaryAlong,
        ),
        wellVolume: layer.startWellDropVolume,
      };
    });
};

export const getFragmentNameByDropNumber = (
  ingredientWell: WellIng,
  dropNumber: number,
) => {
  const dropList = ingredientWell?.dropList;
  const dropLayersList = dropList?.filter((x) => x.dropNumber === dropNumber)[0]
    ?.dropLayerList;
  const fragNames: string[] = [];

  dropLayersList?.forEach((dl) => {
    dl.dropLayerFragmentList?.forEach((dlf) => fragNames.push(`${dlf.name} f`));
  });

  return fragNames.join(', ');
};

export const getFragmentScreenNameByDropNumber = (
  layers: DrawingLayer[],
  ingredientWell: WellIng,
  dropNumber: number,
) => {
  const drop = ingredientWell?.dropList?.find((d) => d.dropNumber === dropNumber);
  if (!drop) {
    return;
  }

  const matchedLayer = layers?.find(
    (layer) =>
      (layer.layerDetail as FragmentLayerDTO)?.fragmentScreenId === drop.fragmentScreenId,
  );

  const fragmentScreenName = (matchedLayer?.layerDetail as FragmentLayerDTO)
    ?.fragmentScreen?.treeNode?.name;

  if (!fragmentScreenName) {
    return;
  }

  return `${fragmentScreenName} f`;
};

export const getSeedNamesByDropNumber = (ingredientWell: WellIng, dropNumber: number) => {
  const seeds = GetSeedsDict();
  const dropList = ingredientWell?.dropList;
  const seedIds = dropList?.find((x) => x.dropNumber === dropNumber)?.seedIDs;
  return (
    seedIds
      ?.map((id) => seeds[id]?.name)
      .filter((name) => !!name)
      .map((name) => `${name} s`)
      .join(' + ') ?? ''
  );
};

export const getWellScreenLayerName = (layers, wellData: WellIng) => {
  const wellLayerIds = wellData.wellLayerList.map((wl) => wl.layerId);
  const wellScreenLayer = layers.find(
    (l) => l.type === LayerType.Screen && wellLayerIds.includes(l.id),
  );

  return wellScreenLayer ? wellScreenLayer.name : '';
};

const hasPositiveValue = (value?: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export const getDropVolumeText = (
  drop?: {
    dropProteinVolume?: number | null;
    dropWellVolume?: number | null;
    dropFragmentVolume?: number | null;
    dropSeedVolume?: number | null;
  },
  dropScreenNamesAndVolumes?: Array<{ volume?: number | null }>,
  dropProteinVolumes?: Array<{
    proteinVolume?: number | null;
    wellVolume?: number | null;
  }>,
  dropSeedVolumes?: Array<{ seedVolume?: number | null; wellVolume?: number | null }>,
): string => {
  if (!drop) {
    return '';
  }

  const parts: string[] = [];

  if (dropProteinVolumes?.length) {
    dropProteinVolumes.forEach((p) => {
      if (hasPositiveValue(p.proteinVolume)) {
        parts.push(`${Round(p.proteinVolume)}µL p`);
      }
      if (hasPositiveValue(p.wellVolume)) {
        parts.push(`${Round(p.wellVolume)}µL w`);
      }
    });
  } else {
    if (hasPositiveValue(drop.dropProteinVolume)) {
      parts.push(`${Round(drop.dropProteinVolume)}µL p`);
    }
    if (hasPositiveValue(drop.dropWellVolume)) {
      parts.push(`${Round(drop.dropWellVolume)}µL w`);
    }
  }

  if (hasPositiveValue(drop.dropFragmentVolume)) {
    parts.push(`${Round(drop.dropFragmentVolume)}µL F`);
  }

  if (dropSeedVolumes?.length) {
    dropSeedVolumes.forEach((s) => {
      if (hasPositiveValue(s.seedVolume)) {
        parts.push(`${Round(s.seedVolume)}µL s`);
      }
      if (hasPositiveValue(s.wellVolume)) {
        parts.push(`${Round(s.wellVolume)}µL w`);
      }
    });
  } else if (hasPositiveValue(drop.dropSeedVolume)) {
    parts.push(`${Round(drop.dropSeedVolume)}µL s`);
  }

  dropScreenNamesAndVolumes
    ?.filter((screenVolume) => hasPositiveValue(screenVolume.volume))
    .forEach((screenVolume) => {
      parts.push(`${Round(screenVolume.volume)}µL a`);
    });

  return parts.length ? `Drop: ${parts.join(' + ')}` : '';
};

export const generateWellTooltipText = (
  wellData: WellIng,
  formatWithPrecision: PrecisionFormatter,
): string => {
  if (!wellData.wellLayerList.length) {
    return null;
  }
  const boldMarker = '\u0001'; // Add after the text that should be bold, will be parsed by tooltip component
  let text = `Water${boldMarker}: ${formatWithPrecision(
    wellData.wellWaterVolume,
    EPrecisionType.Volume,
  )} µL\n`;
  wellData.wellLayerList?.forEach((wellLayer) => {
    wellLayer.wellLayerIngredientList?.forEach((wellLayerIngredient) => {
      const { stock, ingredient } = getIngredientsByIngredientStockId(
        wellLayerIngredient.ingredientStockId,
      );
      text += `${ingredient?.longName}${boldMarker}, `;
      text += `${formatWithPrecision(
        wellLayerIngredient.concentration,
        EPrecisionType.Concentration,
      )}${stock?.concentrationUnits}, `;
      text += `${formatWithPrecision(
        wellLayerIngredient.volume,
        EPrecisionType.Volume,
      )} µL`;
      const pH = formatWithPrecision(wellLayerIngredient.pH, EPrecisionType.PH);
      text += pH ? `, ${pH} pH` : '';
      text += '\n';
    });
  });

  return text.trimEnd();
};
